import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
    Inbox as InboxIcon,
    Send,
    FileText,
    Clock,
    Trash2,
    PenSquare,
    RefreshCw,
    Search,
    Settings,
    Sparkles,
    Loader2,
    Mail,
    Eye,
    MousePointerClick
} from "lucide-react";
import moment from "moment";
import {
    listMessages,
    getThread,
    markRead,
    trashMessage,
    retryMessage,
    cancelMessage,
    unreadCount,
    listMailboxes,
    syncMailbox,
    aiMailDraft
} from "../services/crmServices/mailService";
import { ComposeEmailDialog } from "../components/mail/ComposeEmailDialog";
import { MailboxesPanel } from "../components/mail/MailboxesPanel";
import { MailTemplatesPanel } from "../components/mail/MailTemplatesPanel";
import { MailLibraryPanel } from "../components/mail/MailLibraryPanel";

const FOLDERS = [
    { id: "inbox", label: "Inbox", icon: InboxIcon },
    { id: "sent", label: "Sent", icon: Send },
    { id: "drafts", label: "Drafts", icon: FileText },
    { id: "scheduled", label: "Scheduled", icon: Clock },
    { id: "trash", label: "Trash", icon: Trash2 }
];

function sanitizeEmailHtml(html) {
    return String(html || "")
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
        .replace(/<object[\s\S]*?<\/object>/gi, "")
        .replace(/\son\w+=(?:"[^"]*"|'[^']*')/gi, "")
        .replace(/javascript:/gi, "");
}

function previewText(msg) {
    const raw = msg.text || String(msg.html || "").replace(/<[^>]+>/g, " ");
    return raw.replace(/\s+/g, " ").trim().slice(0, 90);
}

export function Inbox() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [section, setSection] = useState("mail");
    const [folder, setFolder] = useState("inbox");
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState([]);
    const [composeOpen, setComposeOpen] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [draft, setDraft] = useState(null);
    const [mailboxes, setMailboxes] = useState([]);
    const [aiBusy, setAiBusy] = useState(false);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listMessages({ folder, q, page: 1, limit: 50 });
            setRows(res.data?.rows || []);
            setUnread(res.data?.unread || 0);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to load mail");
        } finally {
            setLoading(false);
        }
    }, [folder, q]);

    useEffect(() => {
        if (section === "mail") loadList();
    }, [loadList, section]);

    useEffect(() => {
        listMailboxes()
            .then((res) => setMailboxes(res.data || []))
            .catch(() => {});
        unreadCount()
            .then((res) => setUnread(res.data?.unread || 0))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const id = params.get("message");
        if (!id) return;
        setSection("mail");
        getThread(id)
            .then((res) => {
                const list = res.data || [];
                setThread(list);
                const msg = list.find((m) => m._id === id) || list[list.length - 1];
                setSelected(msg || null);
                if (msg && !msg.read) markRead(msg._id, true).catch(() => {});
            })
            .catch(() => {});
    }, [params]);

    const openMessage = async (msg) => {
        setSelected(msg);
        if (["draft", "failed"].includes(msg.status) || msg.folder === "drafts") {
            setDraft(msg);
            setReplyTo(null);
            setComposeOpen(true);
            return;
        }
        try {
            const res = await getThread(msg._id);
            setThread(res.data || [msg]);
            if (!msg.read && msg.folder === "inbox") {
                await markRead(msg._id, true);
                setRows((prev) => prev.map((r) => (r._id === msg._id ? { ...r, read: true } : r)));
                setUnread((n) => Math.max(0, n - 1));
            }
        } catch (_) {
            setThread([msg]);
        }
    };

    const syncAll = async () => {
        if (!mailboxes.length) return toast.error("Add a mailbox first");
        setLoading(true);
        try {
            for (const box of mailboxes) {
                await syncMailbox(box._id);
            }
            toast.success("Inbox synced");
            await loadList();
        } catch (err) {
            toast.error(err.response?.data?.message || "Sync failed");
        } finally {
            setLoading(false);
        }
    };

    const handleTrash = async (id) => {
        await trashMessage(id);
        setSelected(null);
        setThread([]);
        loadList();
    };

    const insertAi = async (tone, message) => {
        setAiBusy(true);
        try {
            const res = await aiMailDraft({
                messageId: message._id,
                leadId: message.leadId?._id || message.leadId,
                tone
            });
            if (res.status !== 200) throw new Error(res.message);
            setReplyTo({
                ...message,
                _aiPrefill: res.data
            });
            setDraft({
                subject: res.data.subject,
                html: res.data.html,
                to: message.direction === "inbound" ? [{ address: message.from?.address }] : message.to,
                mailboxId: message.mailboxId,
                leadId: message.leadId?._id || message.leadId,
                threadKey: message.threadKey,
                inReplyTo: message.messageId
            });
            setComposeOpen(true);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "AI failed");
        } finally {
            setAiBusy(false);
        }
    };

    const last = thread[thread.length - 1] || selected;
    const emptyMailbox = mailboxes.length === 0;

    const folderCounts = useMemo(() => ({ inbox: unread }), [unread]);

    return (
        <div className="h-[calc(100vh-28px)] -mx-1 flex flex-col">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Inbox</h1>
                    <p className="text-xs text-gray-500">Your email — send, schedule, and reply with AI</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSection("mailboxes")}>
                        <Settings className="h-4 w-4 mr-1" /> Mailboxes
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setSection("templates")}>
                        <FileText className="h-4 w-4 mr-1" /> Templates
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setSection("library")}>
                        Library
                    </Button>
                    <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => {
                            setReplyTo(null);
                            setDraft(null);
                            setComposeOpen(true);
                        }}
                    >
                        <PenSquare className="h-4 w-4 mr-1" /> Compose
                    </Button>
                </div>
            </div>

            {emptyMailbox && section === "mail" && (
                <div className="mb-3 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2">
                    Connect your email under <button type="button" className="underline font-medium" onClick={() => setSection("mailboxes")}>Mailboxes</button> (SMTP + IMAP). The company SMTP is not used for agent mail.
                </div>
            )}

            {section === "mailboxes" && (
                <div className="flex-1 min-h-0">
                    <MailboxesPanel onChanged={setMailboxes} />
                </div>
            )}
            {section === "templates" && (
                <div className="flex-1 min-h-0">
                    <MailTemplatesPanel />
                </div>
            )}
            {section === "library" && (
                <div className="flex-1 min-h-0">
                    <MailLibraryPanel />
                </div>
            )}

            {section === "mail" && (
                <div className="flex-1 min-h-0 grid grid-cols-12 gap-3">
                    <div className="col-span-12 md:col-span-2 bg-white border border-gray-200 rounded-lg p-2 space-y-1">
                        {FOLDERS.map((f) => {
                            const Icon = f.icon;
                            return (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => {
                                        setFolder(f.id);
                                        setSelected(null);
                                        setThread([]);
                                        setSection("mail");
                                    }}
                                    className={`w-full flex items-center justify-between px-2 py-2 rounded-md text-sm ${
                                        folder === f.id ? "bg-red-50 text-red-700" : "text-gray-600 hover:bg-gray-50"
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        <Icon className="h-4 w-4" /> {f.label}
                                    </span>
                                    {f.id === "inbox" && folderCounts.inbox > 0 && (
                                        <span className="text-[10px] bg-red-600 text-white rounded-full px-1.5">{folderCounts.inbox}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="col-span-12 md:col-span-4 bg-white border border-gray-200 rounded-lg flex flex-col min-h-0">
                        <div className="p-2 border-b flex gap-2">
                            <div className="relative flex-1">
                                <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-gray-400" />
                                <Input className="h-8 pl-7 text-sm" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
                            </div>
                            <Button variant="outline" size="sm" onClick={syncAll} disabled={loading}>
                                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            </Button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {rows.map((m) => (
                                <button
                                    key={m._id}
                                    type="button"
                                    onClick={() => openMessage(m)}
                                    className={`w-full text-left px-3 py-2.5 border-b hover:bg-gray-50 ${
                                        selected?._id === m._id ? "bg-red-50" : ""
                                    } ${!m.read && m.folder === "inbox" ? "font-semibold" : ""}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm truncate">
                                            {m.folder === "inbox" ? m.from?.name || m.from?.address : (m.to?.[0]?.address || "")}
                                        </span>
                                        <span className="text-[10px] text-gray-400 shrink-0">
                                            {moment(m.sentAt || m.scheduledAt || m.createdAt).format("MMM D")}
                                        </span>
                                    </div>
                                    <p className="text-xs truncate text-gray-800">{m.subject || "(no subject)"}</p>
                                    <p className="text-[11px] text-gray-400 truncate">{previewText(m)}</p>
                                </button>
                            ))}
                            {!loading && rows.length === 0 && (
                                <p className="text-sm text-gray-400 p-6 text-center">Nothing here yet</p>
                            )}
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-6 bg-white border border-gray-200 rounded-lg flex flex-col min-h-0">
                        {!last && (
                            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                                <Mail className="h-8 w-8 mr-2 opacity-40" /> Select a message
                            </div>
                        )}
                        {last && (
                            <>
                                <div className="p-4 border-b">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h2 className="text-base font-semibold">{last.subject || "(no subject)"}</h2>
                                            {last.leadId && (
                                                <button
                                                    type="button"
                                                    className="text-xs text-red-700 underline"
                                                    onClick={() => navigate(`/leads/edit/${last.leadId._id || last.leadId}`)}
                                                >
                                                    Open lead {last.leadId.firstName || ""} {last.leadId.lastName || ""}
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex gap-1">
                                            {last.status === "failed" && (
                                                <Button size="sm" variant="outline" onClick={() => retryMessage(last._id).then(loadList)}>
                                                    Retry
                                                </Button>
                                            )}
                                            {["queued", "scheduled"].includes(last.status) && (
                                                <Button size="sm" variant="outline" onClick={() => cancelMessage(last._id).then(loadList)}>
                                                    Cancel
                                                </Button>
                                            )}
                                            <Button size="sm" variant="outline" onClick={() => handleTrash(last._id)}>
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    {last.status === "sent" && (
                                        <div className="flex gap-3 mt-2 text-[11px] text-gray-500">
                                            <span className="inline-flex items-center gap-1">
                                                <Eye className="h-3 w-3" /> {last.opens?.length || 0} opens
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <MousePointerClick className="h-3 w-3" /> {last.clicks?.length || 0} clicks
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                    {(thread.length ? thread : [last]).map((m) => (
                                        <div key={m._id} className="border rounded-lg p-3">
                                            <div className="flex justify-between text-xs text-gray-500 mb-2">
                                                <span>
                                                    <strong className="text-gray-800">{m.from?.name || m.from?.address}</strong>
                                                    {" → "}
                                                    {(m.to || []).map((a) => a.address).join(", ")}
                                                </span>
                                                <span>{moment(m.sentAt || m.createdAt).format("MMM D, YYYY HH:mm")}</span>
                                            </div>
                                            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(m.html || `<p>${m.text || ""}</p>`) }} />
                                        </div>
                                    ))}
                                </div>
                                <div className="p-3 border-t flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        className="bg-red-600 hover:bg-red-700"
                                        onClick={() => {
                                            setDraft(null);
                                            setReplyTo(last);
                                            setComposeOpen(true);
                                        }}
                                    >
                                        Reply
                                    </Button>
                                    {["default", "shorter", "warmer", "formal"].map((tone) => (
                                        <Button key={tone} size="sm" variant="outline" disabled={aiBusy} onClick={() => insertAi(tone, last)}>
                                            {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                                            {tone === "default" ? "AI reply" : tone}
                                        </Button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <ComposeEmailDialog
                open={composeOpen}
                onClose={() => {
                    setComposeOpen(false);
                    setReplyTo(null);
                    setDraft(null);
                }}
                replyTo={replyTo}
                draft={draft}
                lead={last?.leadId && typeof last.leadId === "object" ? last.leadId : null}
                onSent={() => loadList()}
            />
        </div>
    );
}
